import { useState } from 'react';
import { message } from 'antd';
import { FormInstance } from 'antd';
import { I18n } from '@aws-amplify/core';

import { useRateLimiting } from './useRateLimiting';
import { validateEmployeeCode } from '../validation';
import { useGeneratePin } from '@/hooks/useGeneratePin';

interface UsePinGenerationProps {
	fillPinAutomatically: (pin: string) => void;
	formInstance: FormInstance;
	surveyData?: {
		survey_link_id?: string;
		survey_id?: string;
	};
	isAnonymous: boolean;
	token?: string;
}

export function usePinGeneration({
	fillPinAutomatically,
	formInstance,
	isAnonymous,
	surveyData,
	token
}: UsePinGenerationProps) {
	const [pinMessage, setPinMessage] = useState('');
	const { isRequestLimited, applyRateLimiting } = useRateLimiting();
	const { mutateAsync: generatePin, isLoading: isGeneratingPin } = useGeneratePin();

	async function handleGeneratePin(): Promise<void> {
		if (isRequestLimited) {
			message.warning(I18n.get('Too many attempts. Please try again later.'));
			return;
		}

		if (!validateEmployeeCode(formInstance, isAnonymous)) return;

		const response = await generatePin({
			employee_code: !isAnonymous ? formInstance.getFieldValue('employee_code') : undefined,
			survey_link_id: surveyData?.survey_link_id || '',
			survey_id: surveyData?.survey_id || '',
			token_survey: token || ''
		});

		applyRateLimiting();

		response.pin && fillPinAutomatically(response.pin);

		if (!isAnonymous) {
			setPinMessage(
				response.pin
					? I18n.get(
							'No registered email was found to send the PIN. It was generated and filled automatically.'
					  )
					: I18n.get('Your access PIN has been sent by email. It is valid for 15 minutes.')
			);
		}
	}

	return {
		pinMessage,
		isGeneratingPin,
		isRequestLimited,
		handleGeneratePin
	};
}
